import { forwardRef, useRef, CSSProperties, useLayoutEffect, useCallback } from 'react'
import { mergeRefs } from 'react-advanced-cropper'
import { observer } from 'mobx-react-lite'

import { EditContext } from './types'

type EditableImageProps = {
    className?: string
    src?: string
    context: EditContext
    style: CSSProperties
}

export const EditableImage = observer(
    forwardRef<HTMLCanvasElement, EditableImageProps>(({ className, src, context, style }: EditableImageProps, ref) => {
        const imageRef = useRef<HTMLImageElement>(null)
        const canvasRef = useRef<HTMLCanvasElement>(null)

        const drawImage = useCallback(() => {
            const image = imageRef.current
            const canvas = canvasRef.current
            if (canvas && image && image.complete) {
                const ctx = canvas.getContext('2d')
                canvas.width = image.naturalWidth
                canvas.height = image.naturalHeight

                if (ctx) {
                    ctx.filter = [
                        `saturate(${context.saturation}%)`,
                        `contrast(${context.contrast}%)`,
                        `brightness(${context.brightness}%)`,
                        `hue-rotate(${(context.hue / 100) * 360}deg)`,
                    ].join(' ')

                    ctx.drawImage(image, 0, 0, image.naturalWidth, image.naturalHeight)
                }
            }
        }, [context.brightness, context.contrast, context.saturation, context.hue])

        useLayoutEffect(() => {
            drawImage()
        }, [drawImage])

        return (
            <>
                <canvas
                    ref={mergeRefs([ref, canvasRef])}
                    className={['advanced-cropper-background-image', className].filter(Boolean).join(' ')}
                    style={style}
                />
                {src && <img ref={imageRef} className="hidden" src={src} crossOrigin="anonymous" onLoad={drawImage} />}
            </>
        )
    })
)
